import { PlusOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import { DndContext, closestCenter } from "@dnd-kit/core";
import { SortableContext } from "@dnd-kit/sortable";
import { Button, Card, Spin, Tooltip, theme } from "antd";
import { cascadeApi } from "api/TenantSlug/Cascade/api";
import {
    useBatchUpdateCascadeMutation,
    useBatchDeleteCascadeMutation,
    useBatchStoreCascadeMutation,
} from "api/TenantSlug/Cascade/hooks.ts";
import { CascadeItem } from "components/other/CascadeItem/CascadeItem.tsx";
import { saveFiltersToLocalStorage, filterAtom } from "components/UI/BaseFilter/atoms/filterAtom.ts";
import { useBaseFilter } from "components/UI/BaseFilter/hooks/useBaseFilter.ts";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider.tsx";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm.ts";
import { useFetchAllPaginatedData } from "hooks/useFetchAllPaginatedData.ts";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { FC, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useRecoilValue } from "recoil";
import { CascadeAction, CascadeDistributionType, CascadeStatus } from "types/enums";
import { FormId } from "types/models";
import { isUUID } from "utils/isUUID";
import { updateStateOnDragEnd } from "utils/updateStateOnDragEnd";

import { ChannelItemFormData, pingtreeFormSchema, PingtreeFormData } from "./formSchema.ts";

import UpdateCascadeRequest = App.Http.Requests.Cascade.UpdateCascadeRequest;
import StoreCascadeRequest = App.Http.Requests.Cascade.StoreCascadeRequest;

interface PingtreeFormContentProps {
    items: string[];
    setItems: React.Dispatch<React.SetStateAction<string[]>>;
    deletedIds: (number | string)[];
    setDeletedIds: React.Dispatch<React.SetStateAction<(number | string)[]>>;
    lastTempId: number;
    setLastTempId: React.Dispatch<React.SetStateAction<number>>;
    cascadesData: App.Data.CascadeData[] | undefined;
    isLoading: boolean;
}

const PingtreeFormContent: FC<PingtreeFormContentProps> = ({
    items,
    setItems,
    deletedIds,
    setDeletedIds,
    lastTempId,
    setLastTempId,
    cascadesData,
    isLoading,
}) => {
    const { useToken } = theme;
    const { token } = useToken();
    const baseFilterUtils = useBaseFilter();
    const { t: tP } = usePaldockTranslation("pages.offer.edit.pingtree.columns");
    const { t: tC } = usePaldockTranslation("common");
    const { t: tG } = usePaldockTranslation("general");
    const [visibleCascadeFilters, setVisibleCascadeFilters] = useState<Set<string>>(new Set());

    const { setValue, getValues, reset } = useGeneralForm<PingtreeFormData>();

    // Initialize form values when cascades data changes
    useEffect(() => {
        if (cascadesData && cascadesData.length > 0) {
            const cascadesIds: string[] = [];
            const channelsData: Record<string, ChannelItemFormData> = {};

            cascadesData.forEach((c: App.Data.CascadeData) => {
                cascadesIds.push(c.id);
                channelsData[c.id] = {
                    title: c.title,
                    visible_title: c.visible_title,
                    integration: c.integration_id ?? "",
                    distribution: c.distribution,
                    action: c.action,
                    active: c.status === CascadeStatus.Enabled,
                };
            });

            setItems(cascadesIds);
            reset({ channels: channelsData });
        } else if (cascadesData) {
            // If cascadesData is empty array, reset everything
            setItems([]);
            reset({ channels: {} });
        }
    }, [cascadesData, setItems, reset]);

    const distributionOptions = useMemo(() => {
        const options = [];
        const distributionEntries = Object.entries(CascadeDistributionType);
        for (const [, value] of distributionEntries) {
            options.push({ label: tG(`cascade_distribution_type.${value}`), value: value });
        }
        return options;
    }, [tG]);

    const actionOptions = useMemo(() => {
        const options = [];
        const distributionEntries = Object.entries(CascadeAction);
        for (const [, value] of distributionEntries) {
            options.push({ label: tG(`cascade_action.${value}`), value: value });
        }
        return options;
    }, [tG]);

    const toggleCascadeFilter = (cascadeId: string) => {
        setVisibleCascadeFilters((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(cascadeId)) {
                newSet.delete(cascadeId);
            } else {
                newSet.add(cascadeId);
            }
            return newSet;
        });
    };

    const getCascadeFilterCount = (cascadeId: string) => {
        const filters = baseFilterUtils.getCascadeFilters(cascadeId);
        const filterGroups = baseFilterUtils.getCascadeFilterGroups(cascadeId);
        return filters.length + filterGroups.length;
    };

    const handleDeleteCascade = (id: string) => {
        if (!isUUID(id)) {
            setItems((prevState) => prevState.filter((item) => item !== id));

            // Clear form field for this item
            const channels = getValues("channels") || {};
            delete channels[id];
            setValue("channels", { ...channels });
        } else {
            setDeletedIds((prevState) => [...prevState, id]);

            // Clear form field
            const channels = getValues("channels") || {};
            delete channels[id];
            setValue("channels", { ...channels });
        }

        try {
            baseFilterUtils.clearCascadeFilters(id.toString());
        } catch (e) {
            console.warn("Failed to clear filters for deleted cascade", id, e);
        }
        setVisibleCascadeFilters((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
        });
    };

    const handleAddCascade = () => {
        const newId = `tmp-${lastTempId}`;
        setItems((prevState) => [...prevState, newId]);
        setLastTempId((prevState) => prevState + 1);

        // Initialize empty form values for new cascade
        const channels = getValues("channels") || {};
        channels[newId] = {
            title: "",
            visible_title: "",
            integration: "",
            distribution: "",
            action: "",
            active: false,
        };
        setValue("channels", { ...channels });
    };

    return (
        <Card>
            {isLoading ? (
                <div className="flex flex-row py-4 justify-center text-center px-2">
                    <Spin />
                </div>
            ) : (
                <>
                    <div className="flex justify-between flex-row py-4 font-semibold gap-x-4 px-2 pl-11">
                        <div className="grid grow grid-cols-5 gap-x-4">
                            <div>
                                <span>{tP("system_name")}</span>
                                <Tooltip placement={"right"} title={tP("system_name")} className="ml-1">
                                    <QuestionCircleOutlined />
                                </Tooltip>
                            </div>
                            <div>
                                <span>{tP("visible_name")}</span>
                                <Tooltip placement={"right"} title={tP("visible_name")} className="ml-1">
                                    <QuestionCircleOutlined />
                                </Tooltip>
                            </div>
                            <div>
                                <span>{tP("integration")}</span>
                                <Tooltip placement={"right"} title={tP("integration")} className="ml-1">
                                    <QuestionCircleOutlined />
                                </Tooltip>
                            </div>
                            <div>
                                <span>{tP("distribution")}</span>
                                <Tooltip placement={"right"} title={tP("distribution")} className="ml-1">
                                    <QuestionCircleOutlined />
                                </Tooltip>
                            </div>
                            <div>
                                <span>{tP("redirect")}</span>
                                <Tooltip placement={"right"} title={tP("redirect")} className="ml-1">
                                    <QuestionCircleOutlined />
                                </Tooltip>
                            </div>
                        </div>
                        <div className="flex flex-row w-80 gap-[2.7rem] shrink-0">
                            <span>{tP("provision")}</span>
                            <span>{tP("filter")}</span>
                            <span>{tP("active")}</span>
                        </div>
                    </div>

                    <DndContext
                        collisionDetection={closestCenter}
                        onDragEnd={(event) => updateStateOnDragEnd(event, setItems)}
                    >
                        {items && (
                            <SortableContext items={items}>
                                <ul className="flex flex-col gap-y-2">
                                    {items.map((id) => (
                                        <CascadeItem
                                            key={id}
                                            id={id}
                                            deletedIds={deletedIds}
                                            visibleCascadeFilters={visibleCascadeFilters}
                                            distributionOptions={distributionOptions}
                                            actionOptions={actionOptions}
                                            getCascadeFilterCount={getCascadeFilterCount}
                                            toggleCascadeFilter={toggleCascadeFilter}
                                            handleDeleteCascade={handleDeleteCascade}
                                        />
                                    ))}
                                </ul>
                            </SortableContext>
                        )}
                    </DndContext>

                    <Button
                        type="primary"
                        size={"middle"}
                        icon={<PlusOutlined />}
                        style={{ marginTop: token.marginMD }}
                        onClick={handleAddCascade}
                    >
                        {tC("add")}
                    </Button>
                </>
            )}
        </Card>
    );
};

interface PingtreeFormProps {
    offerId: string;
}

export const PingtreeForm: FC<PingtreeFormProps> = ({ offerId }) => {
    const [items, setItems] = useState<Array<string>>([]);
    const [lastTempId, setLastTempId] = useState(0);
    const [deletedIds, setDeletedIds] = useState<Array<number | string>>([]);
    const filterState = useRecoilValue(filterAtom);
    const { t: tF } = usePaldockTranslation("forms.pingtree-form");

    const cascadeFormId = FormId.CascadeForm;

    // Fetch all cascades using the hook
    const getCascadesQuery = useFetchAllPaginatedData(
        (payload) => cascadeApi.index(payload),
        {
            filter: {
                filter: {
                    offer_id: offerId,
                },
                sort: "sort",
            },
        },
        "cascades"
    );

    const batchUpdateCascadeMutation = useBatchUpdateCascadeMutation({
        onError: () => toast.error("Failed to update cascade"),
    });

    const batchDeleteCascadeMutation = useBatchDeleteCascadeMutation({
        onError: () => toast.error("Failed to delete cascades"),
    });

    const batchStoreCascadeMutation = useBatchStoreCascadeMutation({
        onError: () => toast.error("Failed to add cascades"),
    });

    const handleSubmit = async (values: PingtreeFormData) => {
        const cascadesToUpdate: UpdateCascadeRequest[] = [];
        const cascadesToStore: StoreCascadeRequest[] = [];
        const channels = values.channels || {};

        try {
            // Batch delete all cascades that have UUIDs (existing cascades)
            const cascadeIdsToDelete = deletedIds.filter((id) => isUUID(id.toString())).map((id) => id.toString());
            if (cascadeIdsToDelete.length > 0) {
                await new Promise<void>((resolve, reject) => {
                    batchDeleteCascadeMutation.mutate(
                        { ids: cascadeIdsToDelete },
                        {
                            onSuccess: () => resolve(),
                            onError: (error) => reject(error),
                        }
                    );
                });
            }

            Object.keys(channels).forEach((key) => {
                // Work only with undeleted
                if (!deletedIds.includes(key)) {
                    const channelData = channels[key];
                    const request = {
                        sort: items.findIndex((item) => item === key),
                        integration_id: channelData.integration,
                        title: channelData.title,
                        visible_title: channelData.visible_title,
                        distribution: channelData.distribution as CascadeDistributionType,
                        action: channelData.action as CascadeAction,
                        status: channelData.active ? CascadeStatus.Enabled : CascadeStatus.Disabled,
                    };

                    // Existing cascades (UUIDs) - add to update batch
                    if (isUUID(key)) {
                        const updateRequest = { ...request, id: key };
                        cascadesToUpdate.push(updateRequest);
                    }
                    // New cascades (non-UUIDs) - add to store batch
                    else {
                        const storeRequest = { ...request, offer_id: offerId };
                        cascadesToStore.push(storeRequest);
                    }
                }
            });

            // Batch update existing cascades
            if (cascadesToUpdate.length > 0) {
                await new Promise<void>((resolve, reject) => {
                    batchUpdateCascadeMutation.mutate(
                        { cascades: cascadesToUpdate },
                        {
                            onSuccess: () => resolve(),
                            onError: (error) => reject(error),
                        }
                    );
                });
            }

            // Batch store new cascades
            if (cascadesToStore.length > 0) {
                await new Promise<void>((resolve, reject) => {
                    batchStoreCascadeMutation.mutate(
                        { cascades: cascadesToStore },
                        {
                            onSuccess: () => resolve(),
                            onError: (error) => reject(error),
                        }
                    );
                });
            }

            // Save filter state to localStorage
            saveFiltersToLocalStorage(filterState);

            toast.success(tF("messages.update_success"));

            // Reset state after successful save
            setDeletedIds([]);
            setLastTempId(0);

            // Refetch cascades data to show actual server state
            await getCascadesQuery.refetch();
        } catch (error) {
            console.error("Error saving cascades:", error);
        }
    };

    const defaultValues: PingtreeFormData = {
        channels: {},
    };

    return (
        <GeneralFormProvider<PingtreeFormData>
            schema={pingtreeFormSchema}
            defaultValues={defaultValues}
            id={cascadeFormId}
            onSubmit={handleSubmit}
            resetOnSubmit={false}
        >
            <PingtreeFormContent
                items={items}
                setItems={setItems}
                deletedIds={deletedIds}
                setDeletedIds={setDeletedIds}
                lastTempId={lastTempId}
                setLastTempId={setLastTempId}
                cascadesData={getCascadesQuery.data}
                isLoading={getCascadesQuery.isLoading}
            />
        </GeneralFormProvider>
    );
};
